import React, { useState } from 'react';

import {
    Channel,
    Chat,
    MessageInput,
    MessageList,
    Thread,
    Window,
} from 'stream-chat-react';
import 'stream-chat-react/dist/css/v2/index.css';

import MessagingSidebar from './Sidebar/MessagingSidebar';
import { StreamChat } from 'stream-chat';
import {
    Box,
    lighten,
    Paper,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ChannelDetails from './ChannelDetails/ChannelDetails';
import ChannelHeader from './ChannelHeader/ChannelHeader';
import ThreadHeader from './Threads/ThreadHeader';
import UserRole from '../../../shared/AuthRoles';
import PinnedMessages from './ChannelHeader/PinnedMessages';
import { useParams } from 'react-router';

const getCss = (theme: Theme) => `
    .str-chat {
        --str-chat__primary-color: ${theme.palette.info.main};
        --str-chat__message-secondary-color: ${theme.palette.text.secondary};
        --str-chat__channel-search-input-color: inherit;
        --str-chat__active-primary-color: ${theme.palette.text.primary};
        --str-chat__surface-color: ${theme.palette.lightGrey.main};
        --str-chat__secondary-surface-color: ${theme.palette.lightGrey.main};
        --str-chat__primary-surface-color: ${theme.palette.lightGrey.main};
        --str-chat__primary-surface-color-low-emphasis: ${theme.palette.info.main};
        --str-chat__border-radius-circle: 6px;
        --str-chat__avatar-background-color: ${theme.palette.info.main};
        --str-chat__message-bubble-color: ${theme.palette.text.primary};
        --str-chat__own-message-bubble-color: ${theme.palette.text.primary};
        --str-chat__card-attachment-color: ${theme.palette.info.main};
        --str-chat__own-message-reaction-background-color: ${theme.palette.lightGrey.main};
        --str-chat__message-options-color: ${theme.palette.text.secondary};
        --str-chat__date-separator-color: ${theme.palette.text.secondary};
        --str-chat__message-bubble-background-color: ${theme.palette.lightGrey.main};
        --str-chat__own-message-bubble-background-color: ${lighten(theme.palette.info.main, 0.82)};
        --str-chat__deleted-message-background-color: ${theme.palette.lightGrey.main};
        --str-chat__own-message-reaction-background-color: ${lighten(theme.palette.info.main, 0.82)};
        --str-chat__message-input-tools-color: ${theme.palette.textAccent.main};
        --str-chat__message-textarea-color: ${theme.palette.text.primary};
        --str-chat__channel-list-border-inline-end: none;
        background: none;
    }

    .str-chat__channel-search-input::placeholder,
    .str-chat__message-textarea::placeholder {
        color: ${theme.palette.text.secondary};
    }

    .str-chat__channel-search.str-chat__channel-search--inline {
        min-height: auto;
    }

    .str-chat__message--pinned .str-chat__message-bubble::before {
        content: '📌';
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        background-color: papayawhip;
        font-size: 0.6rem;
        width: 1.4rem;
        height: 1.4rem;
        border-radius: 9999px;
        z-index: 1;
        top: -10px;
    }

    .str-chat__channel {
        flex: 1;
        min-width: 0;
        width: 100%;
    }
    
    .str-chat__message--other.str-chat__message--pinned .str-chat__message-bubble::before {
        right: -10px;
    }
    
    .str-chat__message--me.str-chat__message--pinned .str-chat__message-bubble::before {
        left: -10px;
    }

    .str-chat__channel-list-react {
        overflow: unset;
    }
`;

const StreamMessaging = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
    const loggedInUser = UserRole();

    const { id } = useParams();

    const [sidebarOpen, setSidebarOpen] = useState<boolean>(
        isMobile ? !id : true,
    );
    const [detailsOpen, setDetailsOpen] = useState<boolean>(
        !(isMobile || isIpad),
    );

    const showToolbar =
        !loggedInUser?.role.includes('owner') || isMobile || isIpad;

    const handleSidebar = () => {
        if (isMobile) setSidebarOpen(false);
    };

    const handleDetails = () => {
        setDetailsOpen(!detailsOpen);

        // on Ipad close the sidebar when details is opened
        if (isIpad && !isMobile) {
            setSidebarOpen(!sidebarOpen);
        }
    };

    const client = StreamChat.getInstance(
        process.env.REACT_APP_STREAM_API_KEY as string,
    );

    const [pinOpen, setPinOpen] = useState(false);

    const handlePinnedMessages = () => {
        setPinOpen(!pinOpen);
    };

    const shouldSubmit = (event: KeyboardEvent) => {
        // force user to use send button on mobile
        if (isMobile || isIpad) return false;

        return event.key === 'Enter' && !event.shiftKey;
    };

    if (!client) return <div>Loading... </div>;

    return (
        <Box
            sx={{
                mt: '-130px',
                display: 'flex',
                height: 'var(--app-viewport-height)',
            }}
        >
            <style>{getCss(theme)}</style>
            <Chat client={client}>
                <MessagingSidebar open={sidebarOpen} onSelect={handleSidebar} />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minWidth: 0,
                        height: '100%',
                    }}
                >
                    {showToolbar && !(isMobile && sidebarOpen) && <Toolbar />}
                    <Box
                        sx={{
                            display: 'flex',
                            flex: 1,
                            minHeight: 0,
                            py: 2,
                            ml: { xs: 0, md: sidebarOpen ? 2 : 0 },
                        }}
                    >
                        <Channel ThreadHeader={ThreadHeader}>
                            <Window>
                                <Paper
                                    sx={{
                                        display:
                                            isMobile &&
                                            (detailsOpen || sidebarOpen)
                                                ? 'none'
                                                : 'flex',
                                        flexDirection: 'column',
                                        borderRadius: 4,
                                        height: '100%',
                                        mr: { xs: 0, md: detailsOpen ? 2 : 0 },
                                    }}
                                >
                                    <ChannelHeader
                                        handleBack={() => setSidebarOpen(true)}
                                        handleOpen={handleDetails}
                                        handlePinnedMessages={
                                            handlePinnedMessages
                                        }
                                    />
                                    <MessageList />
                                    <MessageInput
                                        disabled={pinOpen}
                                        maxRows={8}
                                        grow
                                        shouldSubmit={shouldSubmit}
                                    />
                                    {!(isMobile || isIpad) && (
                                        <Typography
                                            variant='body3'
                                            sx={{
                                                ml: 'auto',
                                                fontWeight: 'bold',
                                                mr: 8,
                                                mb: 1,
                                                zIndex: 1,
                                            }}
                                        >
                                            Shift + Enter for a new line
                                        </Typography>
                                    )}
                                </Paper>
                                <PinnedMessages
                                    open={pinOpen}
                                    onClose={() => setPinOpen(false)}
                                />
                            </Window>
                            <Thread />
                            {detailsOpen && (
                                <ChannelDetails handleClose={handleDetails} />
                            )}
                        </Channel>
                    </Box>
                </Box>
            </Chat>
        </Box>
    );
};

export default StreamMessaging;
